export const NUMBER_SIGNALS_SEO_TITLE_VARIANTS = Object.freeze({
  defaultFrequencyGap: "default_frequency_gap",
  latestFrequency: "latest_frequency",
  yearFrequency: "year_frequency",
});

const TITLE_KEY_BY_VARIANT = Object.freeze({
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.defaultFrequencyGap]: "seo.numberSignals.title.defaultFrequencyGap",
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.latestFrequency]: "seo.numberSignals.title.latestFrequency",
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.yearFrequency]: "seo.numberSignals.title.yearFrequency",
});

const FALLBACK_TITLE_BY_VARIANT = Object.freeze({
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.defaultFrequencyGap]: "{{lotteryName}} Hot and Cold Numbers — Frequency & Gap Signals",
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.latestFrequency]: "{{lotteryName}} Hot and Cold Numbers — Latest Frequency Signals",
  [NUMBER_SIGNALS_SEO_TITLE_VARIANTS.yearFrequency]: "{{lotteryName}} Hot and Cold Numbers — {{year}} Frequency Signals",
});

const DESCRIPTION_KEY = "seo.numberSignals.description";
const DESCRIPTION_FALLBACK = "See current {{lotteryName}} hot and cold numbers, recent frequency, gap behavior, and number-signal context from the latest public draws. Built for clarity, not prediction.";

const normalizeLotteryName = (value) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || "Lottery";
};

const normalizeYear = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? Math.trunc(numericValue)
    : new Date().getUTCFullYear();
};

const normalizeTitleVariant = (value) => (
  Object.values(NUMBER_SIGNALS_SEO_TITLE_VARIANTS).includes(value)
    ? value
    : NUMBER_SIGNALS_SEO_TITLE_VARIANTS.defaultFrequencyGap
);

const interpolateFallback = (template, values) => (
  template
    .replaceAll("{{lotteryName}}", values.lotteryName)
    .replaceAll("{{year}}", String(values.year))
);

const translate = (t, key, values, fallback) => {
  if (typeof t !== "function") {
    return interpolateFallback(fallback, values);
  }

  return t(key, {
    ...values,
    defaultValue: fallback,
  });
};

export function buildNumberSignalsSeo({
  lotteryId = "",
  lotteryName,
  locale = "en",
  year = new Date().getUTCFullYear(),
  titleVariant = NUMBER_SIGNALS_SEO_TITLE_VARIANTS.defaultFrequencyGap,
  t,
} = {}) {
  const normalizedValues = {
    lotteryName: normalizeLotteryName(lotteryName),
    locale: typeof locale === "string" && locale.trim() ? locale.toLowerCase().trim() : "en",
    year: normalizeYear(year),
  };
  const normalizedVariant = normalizeTitleVariant(titleVariant);
  const normalizedLotteryId = typeof lotteryId === "string"
    ? lotteryId.toLowerCase().trim().replace(/[^a-z0-9]/g, "")
    : "";
  const lotteryTitleKey = normalizedLotteryId
    ? `seo.numberSignals.${normalizedLotteryId}.title`
    : "";
  const lotteryDescriptionKey = normalizedLotteryId
    ? `seo.numberSignals.${normalizedLotteryId}.description`
    : "";
  const title = translate(
    t,
    lotteryTitleKey || TITLE_KEY_BY_VARIANT[normalizedVariant],
    normalizedValues,
    FALLBACK_TITLE_BY_VARIANT[normalizedVariant]
  );
  const description = translate(
    t,
    lotteryDescriptionKey || DESCRIPTION_KEY,
    normalizedValues,
    DESCRIPTION_FALLBACK
  );

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
  };
}
